"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProductMapper = void 0;
const stdlib_core_1 = require("@groupby/stdlib-core");
const map_1 = require("@groupby/stdlib-core/collections/map");
const jsonapi_utils_1 = require("../../../#core/jsonapi-utils");
class ProductMapper {
    static toSerializableImage(image) {
        return {
            id: image.id,
            createdAt: image.createdAt.toISOString(),
            updatedAt: image.updatedAt.toISOString(),
            sourceUrl: image.sourceUrl,
        };
    }
    static fromSerializableImage(image) {
        return {
            id: image.id,
            createdAt: new Date(image.createdAt),
            updatedAt: new Date(image.updatedAt),
            sourceUrl: image.sourceUrl,
        };
    }
    toProduct(productResource) {
        return {
            id: productResource.id,
            createdAt: new Date(productResource.attributes.createdAt),
            updatedAt: new Date(productResource.attributes.updatedAt),
            proprietaryId: productResource.attributes.proprietaryId,
            title: productResource.attributes.title,
            description: productResource.attributes.description,
            url: productResource.attributes.url,
            meta: map_1.fromRecord(productResource.attributes.meta),
            images: productResource.attributes.images.map(ProductMapper.fromSerializableImage),
            classId: jsonapi_utils_1.pluckRelationshipId(productResource.relationships.class),
            variantGroupId: jsonapi_utils_1.pluckRelationshipId(productResource.relationships.variantGroup),
            organizationId: jsonapi_utils_1.pluckRelationshipId(productResource.relationships.organization),
            categoryIds: jsonapi_utils_1.pluckRelationshipIds(productResource.relationships.categories),
            classBlacklist: jsonapi_utils_1.pluckRelationshipIds(productResource.relationships.blacklistedClasses),
        };
    }
    toProductResource(product) {
        return {
            id: product.id,
            type: 'Product',
            attributes: {
                createdAt: product.createdAt.toISOString(),
                updatedAt: product.updatedAt.toISOString(),
                proprietaryId: product.proprietaryId,
                title: product.title,
                description: product.description,
                url: product.url,
                meta: map_1.toRecord(product.meta),
                images: product.images.map(ProductMapper.toSerializableImage),
            },
            relationships: {
                class: jsonapi_utils_1.createRelationship(product.classId, (id) => ({ type: 'Class', id })),
                variantGroup: jsonapi_utils_1.createRelationship(product.variantGroupId, (id) => ({ type: 'VariantGroup', id })),
                organization: jsonapi_utils_1.createRelationship(product.organizationId, (id) => ({ type: 'Organization', id })),
                categories: jsonapi_utils_1.createRelationships(product.categoryIds, (id) => ({ type: 'Category', id })),
                blacklistedClasses: jsonapi_utils_1.createRelationships(product.classBlacklist, (id) => ({ type: 'Class', id })),
            },
        };
    }
    toProductCreation(productCreationResource) {
        return {
            proprietaryId: productCreationResource.attributes.proprietaryId,
            title: productCreationResource.attributes.title,
            description: productCreationResource.attributes.description,
            url: productCreationResource.attributes.url,
            meta: map_1.fromRecord(productCreationResource.attributes.meta),
            images: productCreationResource.attributes.images,
            classId: jsonapi_utils_1.pluckRelationshipId(productCreationResource.relationships.class),
            variantGroupId: jsonapi_utils_1.pluckRelationshipId(productCreationResource.relationships.variantGroup),
            organizationId: jsonapi_utils_1.pluckRelationshipId(productCreationResource.relationships.organization),
            categoryIds: jsonapi_utils_1.pluckRelationshipIds(productCreationResource.relationships.categories),
            classBlacklist: jsonapi_utils_1.pluckRelationshipIds(productCreationResource.relationships.blacklistedClasses),
        };
    }
    toProductCreationResource(productCreation) {
        return {
            type: 'Product',
            attributes: {
                proprietaryId: productCreation.proprietaryId,
                title: productCreation.title,
                description: productCreation.description,
                url: productCreation.url,
                meta: map_1.toRecord(productCreation.meta),
                images: productCreation.images,
            },
            relationships: {
                class: jsonapi_utils_1.createRelationship(productCreation.classId, (id) => ({ type: 'Class', id })),
                variantGroup: jsonapi_utils_1.createRelationship(productCreation.variantGroupId, (id) => ({ type: 'VariantGroup', id })),
                organization: jsonapi_utils_1.createRelationship(productCreation.organizationId, (id) => ({ type: 'Organization', id })),
                categories: jsonapi_utils_1.createRelationships(productCreation.categoryIds, (id) => ({ type: 'Category', id })),
                blacklistedClasses: jsonapi_utils_1.createRelationships(productCreation.classBlacklist, (id) => ({ type: 'Class', id })),
            },
        };
    }
    toProductUpdate(productUpdateResource) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return {
            id: productUpdateResource.id,
            proprietaryId: (_a = productUpdateResource.attributes) === null || _a === void 0 ? void 0 : _a.proprietaryId,
            title: (_b = productUpdateResource.attributes) === null || _b === void 0 ? void 0 : _b.title,
            description: (_c = productUpdateResource.attributes) === null || _c === void 0 ? void 0 : _c.description,
            url: (_d = productUpdateResource.attributes) === null || _d === void 0 ? void 0 : _d.url,
            meta: stdlib_core_1.optionalize(map_1.fromRecord)((_e = productUpdateResource.attributes) === null || _e === void 0 ? void 0 : _e.meta),
            images: (_f = productUpdateResource.attributes) === null || _f === void 0 ? void 0 : _f.images,
            classId: stdlib_core_1.optionalize(jsonapi_utils_1.pluckRelationshipId)((_g = productUpdateResource.relationships) === null || _g === void 0 ? void 0 : _g.class),
            classBlacklist: stdlib_core_1.optionalize(jsonapi_utils_1.pluckRelationshipIds)((_h = productUpdateResource.relationships) === null || _h === void 0 ? void 0 : _h.blacklistedClasses),
        };
    }
    toProductUpdateResource(productUpdate) {
        return jsonapi_utils_1.normalizeResource({
            id: productUpdate.id,
            type: 'Product',
            attributes: {
                proprietaryId: productUpdate.proprietaryId,
                title: productUpdate.title,
                description: productUpdate.description,
                url: productUpdate.url,
                meta: stdlib_core_1.optionalize(map_1.toRecord)(productUpdate.meta),
                images: productUpdate.images,
            },
            relationships: {
                class: jsonapi_utils_1.createRelationship(productUpdate.classId, (id) => ({ type: 'Class', id })),
                blacklistedClasses: jsonapi_utils_1.createRelationships(productUpdate.classBlacklist, (id) => ({ type: 'Class', id })),
            },
        });
    }
}
exports.ProductMapper = ProductMapper;
//# sourceMappingURL=#product-mapper.js.map